import { useEffect, useRef, useState, type KeyboardEvent, type SyntheticEvent } from 'react'
import { useTranslation } from 'react-i18next'
import type { ShotFrameLinkDict, ShotListRowDict, ShotProductionPlanDictYMap, ShotVideoPackDict } from '../../../../generated/nodeDataTypes'
import Modal from '../../../ui/Modal'
import { ShotListTable, type FramingField, type ShotFrameRole, type ShotFrameTarget, type ShotListEmptyState, type ShotListTableProps } from './ShotListTable'
import { ProductionDetails } from './ProductionDetails'

type ExpandedShotListModalProps = Pick<ShotListTableProps,
  'sourceKind' | 'onCellChange' | 'onOpeningTransitionChange' | 'onFrameRelink' |
  'frameChoices' | 'duplicateFrameKeys' | 'onFramePickerActiveChange' | 'selectedFrames' | 'locationParts' | 'boundaryTypes' | 'openingSeamTransition'> & {
  isOpen: boolean
  onClose: () => void
  rows: readonly ShotListRowDict[]
  emptyState?: ShotListEmptyState
  disabled: boolean
  onFramingChange?: (rowId: string, field: FramingField, value: string) => void
  frameColumns: readonly ShotFrameRole[]
  frameLinks: readonly ShotFrameLinkDict[]
  videoPacks?: readonly ShotVideoPackDict[]
  framePreview: (link: ShotFrameLinkDict) => string | undefined
  onFrameFocus: (link: ShotFrameTarget) => void
  productionPlan?: ShotProductionPlanDictYMap
  currentGenerationId?: string
  currentScreenplayFingerprint?: string
  onUndoRedo?: (event: KeyboardEvent<HTMLDivElement>) => void
}

/** A portal outside ReactFlow's zoom transform: node dimensions/edges never change. */
export function ExpandedShotListModal({ isOpen, onClose, onFrameFocus, onUndoRedo, rows, productionPlan, currentGenerationId, currentScreenplayFingerprint, ...tableProps }: ExpandedShotListModalProps) {
  const { t } = useTranslation('canvas')
  const [view, setView] = useState<'table' | 'production'>('table')
  const frameFocusRaf = useRef<number | undefined>(undefined)
  const scrollRef = useRef<HTMLDivElement>(null)
  useEffect(() => () => { if (frameFocusRaf.current !== undefined) cancelAnimationFrame(frameFocusRaf.current) }, [])
  // Empty and populated tables have different focus targets. A generation can
  // finish while this dialog is open, replacing one with the other.
  useEffect(() => { if (isOpen) scrollRef.current?.focus() }, [isOpen, rows.length])
  const focusAfterClosing = (link: ShotFrameTarget) => {
    onClose()
    frameFocusRaf.current = requestAnimationFrame(() => {
      frameFocusRaf.current = undefined
      onFrameFocus(link)
    })
  }
  const isolateKeys = (event: KeyboardEvent<HTMLDivElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z') {
      // SHORTCUT_ID: shotList.undo
      // SHORTCUT_ID: shotList.redo
      onUndoRedo?.(event)
    }
    // Portals bubble through the React node tree, so stop ReactFlow's built-in
    // Arrow/Enter handlers. Escape and Tab need a local handler because this
    // also stops the shared Modal's document-level keyboard listeners.
    event.stopPropagation()
    if (event.key === 'Escape') {
      if (!(event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement || event.target instanceof HTMLSelectElement)) onClose()
      return
    }
    if (event.key !== 'Tab') return
    // The table (and its scroll ref) unmounts in the production view. The
    // keyboard event still originates inside the portal's persistent dialog.
    const dialog = event.target instanceof Element ? event.target.closest('[role="dialog"]') : null
    if (!dialog) return
    const focusable = dialog.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus() }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus() }
  }
  const stop = (event: SyntheticEvent) => event.stopPropagation()
  const isolateClick = (event: SyntheticEvent) => {
    event.stopPropagation()
    if (isOpen && scrollRef.current && !scrollRef.current.closest('[role="dialog"]')?.contains(document.activeElement)) {
      scrollRef.current.focus()
    }
  }
  return <div className="contents" onKeyDown={isolateKeys} onClick={isolateClick} onDoubleClick={stop} onContextMenu={stop}
    onMouseMove={stop} onPointerDown={stop} onMouseDown={stop}>
    <Modal isOpen={isOpen} onClose={onClose} title={t('shotList.title')} size="full" padded={false}>
      {productionPlan && <div role="tablist" aria-label="Shot List details" className="flex gap-2 border-b border-border bg-surface-0 p-3">
        <button type="button" role="tab" aria-selected={view === 'table'} onClick={() => setView('table')}
          className="h-7 rounded-lg border border-input-border bg-surface-1 px-3 text-xs text-foreground hover:bg-surface-3 hover:border-input-border-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20">{t('shotList.title')}</button>
        <button type="button" role="tab" aria-selected={view === 'production'} onClick={() => setView('production')}
          className="h-7 rounded-lg border border-input-border bg-surface-1 px-3 text-xs text-foreground hover:bg-surface-3 hover:border-input-border-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20">{t('shotList.productionGenerate')}</button>
      </div>}
      {view === 'production' && productionPlan
        ? <ProductionDetails plan={productionPlan} rows={rows} currentGenerationId={currentGenerationId} currentScreenplayFingerprint={currentScreenplayFingerprint} />
        : <ShotListTable {...tableProps} rows={rows} scrollRef={scrollRef} logInvalidBoundaries={false} onFrameFocus={focusAfterClosing} />}
    </Modal>
  </div>
}
